import React from "react";
import { useRoutes } from "react-router-dom";
import AuthLayout from "@/components/layout/AuthLayout";
import ViewUser from "@/pages/User/ViewUser";
import AuthValidator from "@/pages/StandAlone/AuthValidator";
import { EStandalonePages, STANDALONE_CONSTANTS } from "@/pages/StandAlone/constants";
import ApprovedProducts from "@/pages/StandAlone/ApprovedProducts";
import { handleErrorClose, useStandaloneParams } from "@/standalone";
import ConfirmActionModal from "@/components/organism/ConfirmActionModal";
import { STANDALONE_BASE_PATH } from "@/constant/global/global.constant";
import StandaloneLayout from "@/components/layout/StandaloneLayout";

import Dashboard from "@/pages/Dashboard";
import Provider from "@/pages/Provider";
import CreateEditProvider from "@/pages/Provider/CrudProvider";
import Operator from "@/pages/Operator";
import VdiTransaction from "@/pages/VdiTransaction";
import QuikPick from "@/pages/QuickPay";
import ViewProvider from "@/pages/Provider/ViewProvider";
import User from "@/pages/User";
import Reconciliation from "@/pages/Reconciliation";
import Products from "@/pages/Products";
import MarketPage from "@/pages/Market";
import AlertsPage from "@/pages/Alert";
import MmsSalesPage from "@/pages/MmsSales";
import SmartCoolerProducts from "@/pages/SmartCoolerProducts";
import CoolerTransaction from "@/pages/CoolerTransaction";
import Login from "@/pages/Auth/Login";
import NotFound from "@/pages/NotFound";
import Forbidden from "@/pages/Forbidden";
import Device from "@/pages/Device";
import Profile from "@/pages/Profile";
import CronJobsTracker from "@/pages/CronJobsTracker";
import TargetedReconciliation from "@/pages/TargetedReconciliation";
import SmartCooler from "@/pages/SmartCoolers";
import ViewSmartCooler from "@/pages/SmartCoolers/ViewSmartCooler";
import { ROUTE_CONFIG } from "./routes.config";

const Routers = () => {
  const currentPath = window.location.pathname;
  if (currentPath === STANDALONE_BASE_PATH) {
    const { params, showModal, handleModalConfirm, handleModalClose } = useStandaloneParams();

    // Render modal even when params is null (needed for session conflict dialog)
    if (!params) {
      return (
        <ConfirmActionModal
          isOpen={showModal}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          title={STANDALONE_CONSTANTS.SESSION_CONFLICT_MODAL.TITLE}
          description={STANDALONE_CONSTANTS.SESSION_CONFLICT_MODAL.DESCRIPTION}
          confirmLabel={STANDALONE_CONSTANTS.SESSION_CONFLICT_MODAL.CONFIRM_BUTTON}
          cancelLabel={STANDALONE_CONSTANTS.SESSION_CONFLICT_MODAL.CANCEL_BUTTON}
          variant="warning"
        />
      );
    }
    if (!params.sid || !params.sig) {
      return (
        <ConfirmActionModal
          isOpen={!params.sid || !params.sig}
          onClose={handleErrorClose}
          onConfirm={handleErrorClose}
          title={STANDALONE_CONSTANTS.ACCESS_ERROR_MODAL.TITLE}
          description={STANDALONE_CONSTANTS.ACCESS_ERROR_MODAL.DESCRIPTION}
          confirmLabel={STANDALONE_CONSTANTS.ACCESS_ERROR_MODAL.INFO_BUTTON}
          variant="warning"
        />
      );
    }

    if (params && params.invalidNewAuth) {
      return (
        <ConfirmActionModal
          isOpen={params.invalidNewAuth}
          onClose={handleModalClose}
          onConfirm={handleModalClose}
          title={STANDALONE_CONSTANTS.SESSION_NOT_UPDATED_MODAL.TITLE}
          description={STANDALONE_CONSTANTS.SESSION_NOT_UPDATED_MODAL.DESCRIPTION}
          confirmLabel={STANDALONE_CONSTANTS.SESSION_NOT_UPDATED_MODAL.INFO_BUTTON}
          variant="warning"
        />
      );
    }
    return (<AuthValidator
      sid={params.sid}
      sig={params.sig}
      redirectOnSuccess={params.redirectOnSuccess}
    />);
  }
  const element = useRoutes([
    { path: "/", element: <Login /> },
    {
      element: <StandaloneLayout />,
      children: [
        {
          path: EStandalonePages.APPROVED_PRODUCTS,
          element: <ApprovedProducts />
        },
      ],
    },
    {
      element: <AuthLayout />,
      children: [
        { path: ROUTE_CONFIG.DASHBOARD.path, element: <Dashboard /> },
        { path: ROUTE_CONFIG.PROVIDERS.path, element: <Provider /> },
        { path: ROUTE_CONFIG.PROVIDERS_CREATE.path, element: <CreateEditProvider /> },
        { path: ROUTE_CONFIG.PROVIDERS_EDIT.path, element: <CreateEditProvider /> },
        { path: ROUTE_CONFIG.PROVIDERS_VIEW.path, element: <ViewProvider /> },
        { path: ROUTE_CONFIG.OPERATORS.path, element: <Operator /> },
        { path: ROUTE_CONFIG.VDI_TRANSACTIONS.path, element: <VdiTransaction /> },
        { path: ROUTE_CONFIG.QUIKPICK.path, element: <QuikPick /> },
        { path: ROUTE_CONFIG.RECONCILIATION.path, element: <Reconciliation /> },
        { path: ROUTE_CONFIG.PRODUCTS.path, element: <Products /> },
        { path: ROUTE_CONFIG.USERS.path, element: <User /> },
        { path: ROUTE_CONFIG.USERS_VIEW.path, element: <ViewUser /> },
        { path: ROUTE_CONFIG.MARKETS.path, element: <MarketPage /> },
        { path: ROUTE_CONFIG.SMART_COOLER_ALERTS.path, element: <AlertsPage /> },
        { path: ROUTE_CONFIG.SMART_COOLER_PRODUCTS.path, element: <SmartCoolerProducts /> },
        { path: ROUTE_CONFIG.SMART_COOLER_TRANSACTIONS.path, element: <CoolerTransaction /> },
        { path: ROUTE_CONFIG.DEVICES.path, element: <Device /> },
        { path: ROUTE_CONFIG.SALES.path, element: <MmsSalesPage /> },
        { path: ROUTE_CONFIG.PROFILE.path, element: <Profile /> },
        { path: ROUTE_CONFIG.CRONJOBS.path, element: <CronJobsTracker /> },
        { path: ROUTE_CONFIG.TARGETED_RECONCILIATION.path, element: <TargetedReconciliation /> },
        { path: ROUTE_CONFIG.SMART_COOLERS.path, element: <SmartCooler /> },
        { path: ROUTE_CONFIG.SMART_COOLERS_VIEW.path, element: <ViewSmartCooler /> },
      ],
    },
    { path: "forbidden", element: <Forbidden /> },
    { path: "*", element: <NotFound /> },
  ]);

  return element;
};

export default Routers;
